import { useRef } from 'react';
import { exportLayerOriginInfo, getFramework, getTrackFunc, updateLayerRotate, useIdentity } from '@/framework';
import { ITouchKitEvent } from '@/utils/event-kit';
import { getLayerViewCenterPointForDocument } from '@/utils/layer-kit';
import { degreeBetweenPoints } from '@/utils/math-kit';
import { BaseLayerViewType, type ILayerProps, type IPoint } from '@/version-types';

export const useRotateOperate = () => {
    const identity = useIdentity();
    const oldInfo = useRef<ILayerProps>();
    const originLayerInfo = useRef<ReturnType<typeof exportLayerOriginInfo>>();
    const centerPoint = useRef<IPoint>({ x: 0, y: 0 });
    const framework = getFramework(identity);

    return {
        /** 准备工作, 用于存储一些值 */
        beforeRotate(layerId: string) {
            const layer = framework.getLayer(layerId, true);
            oldInfo.current = layer;
            originLayerInfo.current = exportLayerOriginInfo(identity, layerId);
            framework.events.emitBeforeBaseOperate(layer, BaseLayerViewType.ROTATE);
            centerPoint.current = getLayerViewCenterPointForDocument(identity, layerId);
        },

        /**
         * 旋转
         *
         * @param layerId - 图层 ID
         * @param e - 触摸事件对象
         */
        rotate(layerId: string, e: ITouchKitEvent) {
            if (!originLayerInfo.current) return;
            if (!framework.getLayer(layerId)) return;

            framework.operate.setIsBaseOperate(true);

            const originAngle =
                (degreeBetweenPoints(e.startPoint, e.currentPoint, centerPoint.current) / Math.PI) * 180;
            const angle = Math.round(originAngle);
            const { childOriginInfoMap, originInfo } = originLayerInfo.current;
            const rotate = updateLayerRotate(identity, layerId, angle, originInfo, childOriginInfoMap, {
                isStart: e.isStart,
                isEnd: e.isEnd,
                enableAdsorb: true,
            });
            framework.operate.cursorTip.set({
                type: 'rotate',
                x: e.x,
                y: e.y,
                rotate,
            });
            if (e.isEnd) {
                framework.operate.cursorTip.clear();
                framework.operate.setIsBaseOperate(false);
                framework.stage.addOrUpdate(framework.getLayer(layerId, true));
                if (oldInfo.current) {
                    framework.history.write(oldInfo.current, getTrackFunc(identity, oldInfo.current, 'rotate'));
                }
                framework.operate.setActiveLayer(layerId);
            }

            return rotate;
        },
    };
};
